import React from "react";

/**
 * NiftyNews brand mark.
 *
 * The canonical assets live in `frontend/public`:
 *   - `/logo.png`     — full lock-up (icon + "niftynews" wordmark)
 *   - `/favicon.png`  — icon only (green square + up-right arrow)
 *
 * Props:
 *   size:          approximate icon-square height in px. When the wordmark
 *                  is included the whole lock-up is sized so its icon
 *                  square matches this height.
 *   showWordmark:  render the full logo (default) or the icon only
 *   customLogoUrl: admin-uploaded override (used as-is)
 */
const BrandLogo = ({ size = 44, showWordmark = true, customLogoUrl = "", className = "", theme, ...rest }) => {
    // Silence "unused prop" warning while keeping backward-compat with the
    // old `theme` prop callers still pass — theme no longer changes the
    // PNG-based lock-up (asset is dark on transparent).
    void theme;

    if (customLogoUrl) {
        // Custom uploaded logos render ~1.5× the requested icon height so
        // they read well against the header's cream background.
        const displayH = Math.round(size * 1.5);
        return (
            <img
                src={customLogoUrl}
                alt="NiftyNews"
                className={className}
                style={{ height: displayH, width: "auto", maxWidth: size * 8 }}
                data-testid="brand-logo-custom"
                {...rest}
            />
        );
    }

    if (!showWordmark) {
        return (
            <img
                src="/favicon.png"
                alt="NiftyNews"
                className={className}
                style={{ height: size, width: size, borderRadius: 3 }}
                data-testid="brand-logo-icon"
                {...rest}
            />
        );
    }

    // Full lock-up. The source PNG is ~4:1 (icon + wordmark) — sizing by
    // height keeps the icon square at ≈`size` px on any screen density.
    return (
        <img
            src="/logo.png"
            alt="NiftyNews"
            className={className}
            style={{ height: size, width: "auto" }}
            data-testid="brand-logo"
            {...rest}
        />
    );
};

export default BrandLogo;
